import React, { FC, useState } from 'react';
import styled from '@emotion/styled';

import { PopoutMenu, PopoutMenuItem } from '~Scenes/Handy/Components/PopoutMenu';
import { Button } from '~Styles';
import PrinterIcon from '~Assets/icons/print.svg';
import { useButton, useI18n, useLogger, usePrintBill, useSelector } from '~Hooks';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import Icon from '~ComponentsImproved/Icon';
import ReceiptIcon from '~/Assets/icons/receipt_long.svg';
import InvoiceIcon from '~/Assets/icons/receipt.svg';
import { useStaffContext } from '~Scenes/Handy/Context/StaffProvider';
import { useTableUniverse } from '~Components/TableUniverse';

const TriggerButton = styled(Button)`
  z-index: 3;
  width: 64px;
`;

const PrintButton: FC = () => {
  const { i18n } = useI18n();
  const { selectedTable } = useTableContext();
  const { selectedCheque } = useChequeContext();
  const { authedStaff } = useStaffContext();
  const universe = useTableUniverse();
  const logger = useLogger('handy');

  // Used to determine if we should disable Invoice Print in this menu
  const hasInvoiceMethod = useSelector(
    (state) => !!state.POSPaymentMethods.find((pM) => pM.id === 'PRINTED_INVOICE'),
  );
  const deviceWakeState = useSelector((state) => state.deviceWakeState);

  const [printMenuOpen, setPrintMenuOpen] = useState(false);

  // The first cheque on the table is used as a fallback when we do not have a selected cheque
  const firstChequeOnTable = universe.cheques.find(
    (cheque) => cheque.tableId === selectedTable?.id,
  );

  const chequeToPrint = selectedCheque ?? firstChequeOnTable;

  const { printBill, printInvoiceBill } = usePrintBill({
    selectedCheque: chequeToPrint?.id || '',
    selectedTable: { id: selectedTable?.id || '', name: selectedTable?.code || '' },
  });

  const handlePrintBill = () => {
    logger.info('User manually printed bill.', {
      cheque: selectedCheque,
      table: selectedTable,
      staff: authedStaff,
    });
    printBill();
    setPrintMenuOpen(false);
  };

  const handlePrintInvoice = () => {
    // If the venue does not have invoice payment method, abort
    if (!hasInvoiceMethod) {
      return;
    }

    logger.info('User manually printed invoice.', {
      cheque: selectedCheque,
      table: selectedTable,
      staff: authedStaff,
    });
    printInvoiceBill();
    setPrintMenuOpen(false);
  };

  const printButton = useButton({
    onClick: () => setPrintMenuOpen((prev) => !prev),
  });

  const printInvoiceButton = useButton({
    onClick: handlePrintInvoice,
    disabled: deviceWakeState.syncingTableEvents || !hasInvoiceMethod,
    disabledToast: deviceWakeState.syncingTableEvents
      ? i18n('Handy.Tables.LoadingEvents')
      : undefined,
  });

  const printBillButton = useButton({
    onClick: handlePrintBill,
    disabled: deviceWakeState.syncingTableEvents,
    disabledToast: i18n('Handy.Tables.LoadingEvents'),
  });

  if (!selectedTable) {
    console.warn('PrintButton may only be rendered when there is a selected table.');
    return null;
  }

  if (!chequeToPrint) {
    // This can happen if there are no cheques on the table
    return null;
  }

  return (
    <div id="print-button" style={{ position: 'relative' }}>
      <TriggerButton padding="none" background="colorBlueGray50" {...printButton}>
        <Icon src={PrinterIcon} color="var(--colorBlueGray1100)" />
      </TriggerButton>

      <PopoutMenu
        position={selectedCheque ? 'right' : 'left'}
        open={printMenuOpen}
        rootId="print-button"
        closeOnClickOutside
        onClose={() => setPrintMenuOpen(false)}>
        <PopoutMenuItem {...printBillButton}>
          <Icon src={ReceiptIcon} />
          {i18n('TM.Bill')}
        </PopoutMenuItem>
        <PopoutMenuItem {...printInvoiceButton}>
          <Icon src={InvoiceIcon} />
          {i18n('TM.InvoiceBill')}
        </PopoutMenuItem>
      </PopoutMenu>
    </div>
  );
};

export default PrintButton;
